import React, { useMemo, useState } from 'react';

export default function ContactUs({ resumeData }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    company: '',
  });
  const [submitState, setSubmitState] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const contactConfig = useMemo(() => {
    const email = resumeData?.contact?.email || 'johncletusakpan@gmail.com';
    const formEndpoint =
      resumeData?.contact?.formEndpoint || `https://formsubmit.co/ajax/${email}`;

    return {
      email,
      formEndpoint,
      phone: resumeData?.contact?.phone || '(+234) 7068619556',
      location: resumeData?.contact?.location || 'Uyo, Akwa Ibom State, Nigeria',
      label: resumeData?.name || 'John Cletus',
    };
  }, [resumeData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitState('submitting');
    setErrorMessage('');

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        subject: formData.subject || 'Portfolio contact',
        message: formData.message,
        _subject: `Portfolio message from ${formData.name}`,
        _captcha: 'false',
        _template: 'table',
      };

      const response = await fetch(contactConfig.formEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Submission failed.');
      }

      setSubmitState('success');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        company: '',
      });
    } catch (error) {
      setSubmitState('error');
      setErrorMessage('Unable to send message right now. Please try again or email me directly.');
    }
  };

  return (
    <section id="contact" aria-labelledby="contact-title">
      <div className="row section-head">
        <div className="ten columns header-col">
          <h1 id="contact-title">
            <span>Get In Touch.</span>
          </h1>
          <p className="section-subtitle">
            Let&apos;s discuss your product idea, performance improvements, or full stack build.
          </p>
        </div>
      </div>

      <div className="row">
        <div className="eight columns">
          <form
            action={contactConfig.formEndpoint}
            method="POST"
            id="contactForm"
            name="contactForm"
            onSubmit={handleSubmit}
            autoComplete="on"
          >
            <fieldset>
              <div>
                <label htmlFor="name">
                  Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  autoComplete="name"
                  required
                />
              </div>

              <div>
                <label htmlFor="email">
                  Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                  required
                />
              </div>

              <div>
                <label htmlFor="subject">Subject</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  autoComplete="off"
                />
              </div>

              <div>
                <label htmlFor="message">
                  Message <span className="required">*</span>
                </label>
                <textarea
                  cols={50}
                  rows={10}
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  autoComplete="off"
                  required
                />
              </div>

              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                tabIndex="-1"
                autoComplete="off"
                className="contact-honeypot"
                aria-hidden="true"
              />

              <div>
                <button className="submit" type="submit" disabled={submitState === 'submitting'}>
                  {submitState === 'submitting' ? 'Sending...' : 'Submit'}
                </button>
                <span id="image-loader" aria-hidden={submitState !== 'submitting'}>
                  {submitState === 'submitting' && <img alt="Sending" src="images/loader.gif" />}
                </span>
              </div>
            </fieldset>
          </form>

          {submitState === 'error' && (
            <div id="message-warning" role="alert">
              {errorMessage}
            </div>
          )}

          {submitState === 'success' && (
            <div id="message-success" role="status">
              <i className="fa fa-check" /> Your message was sent successfully. Thank you.
            </div>
          )}
        </div>

        <aside className="four columns footer-widgets">
          <div className="widget widget_contact">
            <h4>Address and Phone</h4>
            <p className="address">
              {contactConfig.label}
              <br />
              {contactConfig.location}
              <br />
              <span>{contactConfig.phone}</span>
              <br />
              <a href={`mailto:${contactConfig.email}`}>{contactConfig.email}</a>
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
}
